import React, { useEffect, useState } from "react";
import { LoaderScreen } from "../../components/LoaderScreen";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { capitalizePalabras } from "../../../Helpers/CapitalizePalabras";
import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import SaveIcon from "@mui/icons-material/Save";

export const AnticiposSinUUIDSection = ({ data, botones }) => {
  const [anticipos, setAnticipos]                         = useState([]);
  const [cancelaciones, setCancelaciones]                 = useState([]);
  // Anticipo seleccionado — identificado por serie_fiscal + documento
  const [selectedAnticipo, setSelectedAnticipo]           = useState(null);
  // Cancelación seleccionada — solo una a la vez
  const [selectedCancelacion, setSelectedCancelacion]     = useState(null);
  const [loader, setLoader] = useState({
    mostrar: false,
    alerta:  false,
    error:   false,
    mensaje: "",
  });

  useEffect(() => {
    setCancelaciones([]);
    setSelectedCancelacion(null);
    setSelectedAnticipo(null);
    setAnticipos(data);
  }, [data]);

  const buildAnticipoKey = (serie_fiscal, v_ref, importe) =>
    `${serie_fiscal}||${v_ref}||${importe}`;

  const onSearchCancelaciones = (serie_fiscal, v_ref, importe) => {
    setLoader({ ...loader, mostrar: true });
    request
      .get(
        `ListadoInvoiceMoviemientos/ObtenerListadoPosiblesCancelaciones?serie_fiscal=${encodeURIComponent(serie_fiscal)}&v_ref=${encodeURIComponent(v_ref)}&importe=${encodeURIComponent(importe)}`
      )
      .then((result) => {
        setLoader({ ...loader, mostrar: false });
        setCancelaciones(result ?? []);
        const preseleccionado = (result ?? []).find((x) => x.chk === 1 || x.chk === true);
        setSelectedCancelacion(preseleccionado?.folio ?? null);
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("DASHBOARD FISCAL", JSON.stringify(error), "error");
      });
  };

  const handleClickAnticipo = (anticipo) => {
    const importe = ((anticipo.cargo || 0) + (anticipo.abono || 0)).toFixed(2);
    const key = buildAnticipoKey(anticipo.serie_fiscal, anticipo.v_ref, importe);

    if (selectedAnticipo === key) return;

    setSelectedAnticipo(key);
    setSelectedCancelacion(null);
    onSearchCancelaciones(anticipo.serie_fiscal, anticipo.v_ref, importe);
  };

  const totalSeleccionado = cancelaciones
    .filter((can) => selectedCancelacion === can.folio)
    .reduce((sum, can) => sum + (can.cargo + can.abono || 0), 0);

  const importeAnticipoSeleccionado = (() => {
    if (!selectedAnticipo) return 0;
    const [, , importe] = selectedAnticipo.split("||");
    return parseFloat(importe) || 0;
  })();

  const diferencia =
    selectedCancelacion !== null
      ? Math.abs(importeAnticipoSeleccionado - totalSeleccionado).toFixed(2)
      : 0;

  const onAplicar = () => {
    if (!selectedCancelacion) {
      Swal.fire(
        "CORRECCION DE INCIDENCIAS",
        "Debe seleccionar una cancelación para ligar",
        "warning"
      );
      return;
    }

    if (
      diferencia !== 0 &&
      diferencia !== "0" &&
      diferencia !== "0.0" &&
      diferencia !== "0.00"
    ) {
      Swal.fire(
        "CORRECCION DE INCIDENCIAS",
        "El importe del anticipo seleccionado no coincide con el de la cancelación seleccionada",
        "warning"
      );
      return;
    }

    const serieFiscalCancelacion = cancelaciones.find(
      (can) => can.folio === selectedCancelacion
    )?.serie_fiscal;

    const [serieFiscalAnticipo] = selectedAnticipo.split("||");

    const mdl = {
      serie_fiscal_anticipo:    serieFiscalAnticipo,
      serie_fiscal_cancelacion: serieFiscalCancelacion,
      usuario:                  0,
    };

    setLoader({ ...loader, mostrar: true });
    request
      .post("ListadoInvoiceMoviemientos/GuardarRelacionAnticipoCancelacion", mdl)
      .then(() => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("Éxito", "Relación guardada correctamente", "success");
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("Error", JSON.stringify(error), "error");
      });
  };

  return (
    <div className="listado-fiscal-invoicemovimientos-container">
      <LoaderScreen loader={loader} />

      {/* ── Tabla izquierda: Anticipos sin UUID ── */}
      <div className="listado-fiscal-invoicemovimientos-container-tabla scroll">
        <table>
          <thead>
            <tr>
              <th colSpan={9}>Anticipos Sin UUID</th>
            </tr>
            <tr>
              <th>Sucursal</th>
              <th><div>Cargo</div></th>
              <th><div>Abono</div></th>
              <th><div>Fecha</div></th>
              <th><div>Batch</div></th>
              <th><div>Document No.</div></th>
              <th><div>Serie Fiscal</div></th>
              <th><div>Referencia</div></th>
              <th><div>Usuario</div></th>
            </tr>
          </thead>
          <tbody>
            {anticipos.map((ant, i) => {
              const key      = buildAnticipoKey(ant.serie_fiscal, ant.v_ref, ((ant.cargo || 0) + (ant.abono || 0)).toFixed(2));
              const selected = selectedAnticipo === key;
              return (
                <tr
                  key={i}
                  onClick={() => handleClickAnticipo(ant)}
                  className={selected ? "selected-row" : ""}
                  style={{ cursor: "pointer" }}
                >
                  <td className="ta-center">{capitalizePalabras(ant.sucursal)}</td>
                  <td className="ta-right">{FormatMoney(ant.cargo)}</td>
                  <td className="ta-right">{FormatMoney(ant.abono)}</td>
                  <td className="ta-center">
                    {ant.fecha
                      ? new Date(ant.fecha).toLocaleDateString("es-MX", {
                          day:   "2-digit",
                          month: "2-digit",
                          year:  "numeric",
                        })
                      : "-"}
                  </td>
                  <td className="ta-center">{ant.batch}</td>
                  <td className="ta-center">{ant.document_no}</td>
                  <td className="ta-center">{ant.serie_fiscal}</td>
                  <td className="ta-left">{ant.v_ref}</td>
                  <td className="ta-center">{ant.v_usuario}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Tabla derecha: Posibles cancelaciones ── */}
      <div className="listado-fiscal-invoicemovimientos-container-tabla scroll">
        <table>
          <thead>
            <tr>
              <th colSpan={11}>Posibles Cancelaciones</th>
            </tr>
            <tr>
              <th></th>
              <th>Sucursal</th>
              <th><div>Serie Fiscal</div></th>
              <th><div>Cargo</div></th>
              <th><div>Abono</div></th>
              <th><div>Fecha</div></th>
              <th><div>Estatus</div></th>
              <th><div>Batch</div></th>
              <th><div>Usuario</div></th>
            </tr>
          </thead>
          <tbody>
            {cancelaciones.map((can, i) => {
              const isSelected = selectedCancelacion === can.folio;
              return (
                <tr key={i} className={can.bloqueado ? "block-row" : ""}>
                  <td className="ta-center">
                    <input
                      type="radio"
                      name="radio-cancelaciones-anticipo"
                      checked={isSelected}
                      disabled={can.bloqueado || !botones?.aplicar}
                      onChange={() => setSelectedCancelacion(can.folio)}
                    />
                  </td>
                  <td className="ta-center">{capitalizePalabras(can.sucursal)}</td>
                  <td className="ta-center">{can.serie_fiscal}</td>
                  <td className="ta-right">{FormatMoney(can.cargo)}</td>
                  <td className="ta-right">{FormatMoney(can.abono)}</td>
                  <td className="ta-center">
                    {can.fecha
                      ? new Date(can.fecha).toLocaleDateString("es-MX", {
                          day:   "2-digit",
                          month: "2-digit",
                          year:  "numeric",
                        })
                      : "-"}
                  </td>
                  <td className="ta-center">{can.estatus}</td>
                  <td className="ta-center">{can.batch}</td>
                  <td className="ta-center">{can.usuario}</td>
                </tr>
              );
            })}

            <tr style={{ fontWeight: "bold", background: "#f3f3f3" }}>
              <td colSpan={4} className="ta-right">Diferencia</td>
              <td className="ta-right">{FormatMoney(diferencia)}</td>
              <td colSpan={4}></td>
            </tr>
          </tbody>
        </table>

        {botones?.aplicar && (
          <div className="buttons">
            <button
              className="listado-fiscal-invoicemovimientos-aplicar"
              onClick={onAplicar}
            >
              <SaveIcon /> Aplicar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
